import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("fp_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const money = (n) =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 2 }).format(n || 0);

export const today = () => new Date().toISOString().slice(0, 10);

export const addDays = (d, days) => {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + days);
  return dt.toISOString().slice(0, 10);
};

export const humanDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
};

export const can = (user, perm) => user?.role === "admin" || !!user?.permissions?.[perm];
export const canView = (user, mod) => {
  if (user?.role === "admin") return true;
  if (mod === "banks") return user?.permissions?.view_banks !== false;
  if (mod === "cheques") return user?.permissions?.view_cheques !== false;
  if (mod === "users") return !!user?.permissions?.manage_users;
  if (mod === "service") return !!user?.permissions?.manage_service;
  if (mod === "warranty") return !!user?.permissions?.manage_warranty;
  if (mod === "utts") return true;
  if (mod === "price_list") return !!user?.permissions?.view_price_list;
  if (mod === "payroll") return !!user?.permissions?.manage_payroll;
  return true;
};

export const localDay = (iso) => {
  const d = new Date(iso || "");
  return isNaN(d) ? String(iso || "").slice(0, 10) : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
export const localToday = () => localDay(new Date().toISOString());

export const canEditWorkOrder = (user, w) => {
  if (!user || !w) return false;
  if (user.role === "admin") return true;
  const sameDay = localDay(w.created_at) >= localToday();
  if (w.created_by === user.id && sameDay) return true;
  return !!user.permissions?.edit_work_order && (sameDay || !!user.permissions?.history_work_order);
};

export const download = async (path, filename) => {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 400);
};


/* --------- Telefon ve plaka normalizasyonu --------- */
// Türk cep telefonu formatı: "+90 5XX XXX XX XX"
export const normalizePhoneTR = (raw) => {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  // Yurt dışı: + ile başlıyorsa ve 90 değilse olduğu gibi bırak (temizlenmiş)
  if (String(raw).trim().startsWith("+") && !digits.startsWith("90")) {
    return "+" + digits;
  }
  let core = digits;
  if (core.startsWith("90")) core = core.slice(2);
  else if (core.startsWith("0")) core = core.slice(1);
  if (core.length < 10) return raw; // eksik → dokunma
  core = core.slice(0, 10);
  return `+90 ${core.slice(0, 3)} ${core.slice(3, 6)} ${core.slice(6, 8)} ${core.slice(8, 10)}`;
};

// Türk plakası: "35 SS 3535" formatı
export const normalizePlateTR = (raw) => {
  if (!raw) return "";
  const clean = String(raw).toLocaleUpperCase("tr-TR")
    .replace(/İ/g, "I").replace(/Ğ/g, "G").replace(/Ü/g, "U").replace(/Ş/g, "S").replace(/Ö/g, "O").replace(/Ç/g, "C")
    .replace(/[^A-Z0-9]/g, "");
  const m = clean.match(/^(\d{1,2})([A-Z]{1,3})(\d{1,4})$/);
  if (!m) return clean;
  return `${m[1].padStart(2, "0")} ${m[2]} ${m[3]}`;
};

// Para birimi gösterimi
export const moneyCur = (n, cur = "TRY", goldUnit = "gr") => {
  const code = (cur || "TRY").toUpperCase();
  const sym = { TRY: "₺", USD: "$", EUR: "€", XAU: "gr" };
  const adet = code === "XAU" && goldUnit === "adet";
  const digits = adet && Number.isInteger(Number(n || 0)) ? 0 : 2;
  const formatted = new Intl.NumberFormat("tr-TR", { minimumFractionDigits: digits, maximumFractionDigits: 2 }).format(n || 0);
  if (code === "XAU") return `${formatted} ${adet ? "adet" : "gr"}`;
  return `${sym[code] || code} ${formatted}`;
};

// Altın cinsleri (TL karşılığı için)
export const GOLD_TYPES = [
  { value: "GRA", label: "Gram altın (24 ayar)" }, { value: "HAS", label: "Has altın (gr)" },
  { value: "CEYREKALTIN", label: "Çeyrek altın" }, { value: "YARIMALTIN", label: "Yarım altın" },
  { value: "TAMALTIN", label: "Tam altın" }, { value: "CUMHURIYETALTINI", label: "Cumhuriyet altını" },
  { value: "ATAALTIN", label: "Ata altın (kulplu / tam ata)" }, { value: "RESATALTIN", label: "Reşat altın" },
  { value: "IKIBUCUKALTIN", label: "İkibuçuk altın" }, { value: "BESLIALTIN", label: "Beşli altın" },
  { value: "18AYARALTIN", label: "18 ayar (gr)" }, { value: "14AYARALTIN", label: "14 ayar (gr)" },
];
export const goldTL = (acc, gold) => {
  if (!gold || acc?.currency !== "XAU") return null;
  const key = acc.gold_type || (acc.gold_unit === "adet" ? "ATAALTIN" : "GRA");
  const price = gold.coins?.[key]?.buying || (key === "GRA" ? gold.buying : null);
  return price ? Number(acc.balance || 0) * price : null;
};

// WhatsApp: proforma fişini hazır mesaj + PDF linki ile gönder
export const isUnpaidProforma = (inv) => inv?.invoice_type === "Proforma" && !["Ödendi", "İptal"].includes(inv?.status);

export const waNumber = (raw) => {
  let d = String(raw || "").replace(/\D/g, "");
  if (d.startsWith("0")) d = "90" + d.slice(1);
  if (d.length === 10 && d.startsWith("5")) d = "90" + d;
  return d;
};

export const sendProformaWhatsApp = async (inv) => {
  const win = window.open("", "_blank");
  try {
    const { data } = await api.post(`/invoices/${inv.id}/share`);
    const pdfUrl = `${API}/public/invoices/${inv.id}/pdf?t=${data.token}`;
    const rowTotal = (r) => Number(r.quantity || 0) * Number(r.unit_price || 0) * (1 + Number(r.kdv || 0) / 100);
    const vehicles = (inv.vehicles || []).length ? inv.vehicles : [{ plate: inv.plate, chassis_no: inv.chassis_no }];
    const plates = vehicles.map((v) => v.plate).filter(Boolean).join(", ");
    const items = [...(inv.products || []), ...(inv.services || [])]
      .map((r) => `• ${r.name || r.code || "Kalem"} x${r.quantity} = ${money(rowTotal(r))}`).join("\n");
    const msg = [
      `Sayın ${inv.customer_name},`,
      `${inv.invoice_no} numaralı iş fişiniz (${humanDate(inv.date)}) bilginize sunulur.`,
      plates ? `Plaka: ${plates}` : "",
      items ? `\n${items}` : "",
      `\nToplam: ${money(inv.total)}`,
      inv.due_date ? `Son ödeme: ${humanDate(inv.due_date)}` : "",
      `\nFiş (PDF): ${pdfUrl}`,
      `\nSerhan Elektronik`,
    ].filter(Boolean).join("\n");
    const num = waNumber(data.whatsapp || data.phone);
    const url = `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
    if (win) win.location.href = url; else window.location.href = url;
    return url;
  } catch (e) {
    if (win) win.close();
    throw e;
  }
};

export const sumByCur = (rows, amt, cur = (r) => r.currency) => {
  const m = {};
  rows.forEach((r) => { const c = (cur(r) || "TRY").toUpperCase(); m[c] = (m[c] || 0) + (Number(amt(r)) || 0); });
  const keys = Object.keys(m).sort((a, b) => (a === "TRY" ? -1 : b === "TRY" ? 1 : a.localeCompare(b)));
  return keys.length ? keys.map((c) => moneyCur(m[c], c)).join(" · ") : moneyCur(0, "TRY");
};

// İş fişlerinden (tek veya çoklu) fatura ön doldurma; her araç ayrı bölüm, araçsız fişler -1
const normName = (s) => (s || "").toLocaleLowerCase("tr-TR").replace(/\s+/g, " ").trim();
export const openInvoiceFromWorkOrders = (wos) => {
  const names = [...new Set(wos.map((w) => normName(w.customer_name)))];
  const mismatch = names.length > 1;
  const vehicles = [];
  const vIdx = (w) => {
    if (w.no_vehicle || (!w.plate && !w.chassis_no)) return -1;
    const key = normName(w.plate || w.chassis_no);
    let i = vehicles.findIndex((v) => normName(v.plate || v.chassis_no) === key);
    if (i < 0) { vehicles.push({ plate: normalizePlateTR(w.plate || ""), chassis_no: w.chassis_no || "" }); i = vehicles.length - 1; }
    return i;
  };
  const products = [], services = [];
  wos.forEach((w) => {
    const v = vIdx(w);
    (w.items || []).forEach((it) => {
      const base = { name: it.name || "", description: it.description || "", quantity: Number(it.qty) || 1,
                     unit_price: Number(it.unit_price) || 0, kdv: 20, vehicle_idx: v };
      if (it.is_product && it.stock_id) products.push({ ...base, stock_id: it.stock_id, code: it.code || "", source_warehouse: "main" });
      else services.push({ ...base, service_id: it.service_id || null, code: it.code || "" });
    });
  });
  const lead = wos.find((w) => !w.no_vehicle && (w.owner_id_no || w.ruhsat_photo)) || wos.find((w) => w.owner_id_no) || wos.find((w) => !w.no_vehicle) || wos[0];
  const prefill = {
    customer_name: mismatch ? "" : wos[0].customer_name,
    customer_mismatch: mismatch ? wos.map((w) => w.customer_name).filter((n, i, a) => a.indexOf(n) === i) : null,
    vehicles: vehicles.length ? vehicles : [{ plate: "", chassis_no: "" }],
    plate: vehicles[0]?.plate || "", chassis_no: vehicles[0]?.chassis_no || "",
    note: wos.map((w) => [w.job, w.note].filter(Boolean).join(" · ")).filter(Boolean).join(" | "),
    products, services,
    source_work_order_id: mismatch ? null : lead.id,
    source_work_order_ids: wos.map((w) => w.id),
  };
  try { localStorage.setItem("sp_prefill_invoice", JSON.stringify(prefill)); } catch { /* ignore */ }
  window.location.assign("/?page=invoices&prefill=1");
};

// Aynı sekmede aç; orta tuş / Ctrl / Cmd / Shift ile yeni sekme
const wantsNewTab = (e) => !!e && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1);
export const goTo = (path, e) => {
  if (wantsNewTab(e)) window.open(path, "_blank", "noopener");
  else window.location.assign(path);
};
export const rowLink = (path, stop = false) => ({
  onClick: (e) => { if (stop) e.stopPropagation(); goTo(path, e); },
  onAuxClick: (e) => { if (e.button === 1) { e.preventDefault(); if (stop) e.stopPropagation(); window.open(path, "_blank", "noopener"); } },
  onMouseDown: (e) => { if (e.button === 1) e.preventDefault(); },
});
export const goBack = () => {
  if (window.history.length > 1 && document.referrer && new URL(document.referrer).origin === window.location.origin) window.history.back();
  else window.location.assign("/");
};

